import { lazy, Suspense } from 'react';

import { api } from '../../api.js';
import { Loading } from '../../components/ui.jsx';

/** The support desk in the web app: client tickets, guest tickets, the staff queue and setup. */
const page = (load, name) => () => load().then((m) => ({ Component: m[name] }));
const client = () => import('./ClientTickets.jsx');
const admin = () => import('./AdminTickets.jsx');
const setup = () => import('./SupportSetup.jsx');
const desk = () => import('./DeskSetup.jsx');

const deferred = (load, name) => {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] })));
  return function Deferred(props) {
    return (
      <Suspense fallback={<Loading />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
};

export default {
  name: 'support',
  publicRoutes: [{ path: 'support/guest', lazy: page(client, 'GuestSupport') }],
  clientRoutes: [
    { path: 'tickets', lazy: page(client, 'ClientTickets') },
    { path: 'tickets/new', lazy: page(client, 'NewTicket') },
    { path: 'tickets/:number', lazy: page(client, 'ClientTicket') },
  ],
  adminRoutes: [
    { path: 'tickets', lazy: page(admin, 'AdminTickets') },
    { path: 'tickets/:number', lazy: page(admin, 'AdminTicket') },
    { path: 'support/departments', lazy: page(setup, 'Departments') },
    { path: 'support/canned', lazy: page(setup, 'CannedResponses') },
    { path: 'support/escalations', lazy: page(setup, 'Escalations') },
    { path: 'support/tags', lazy: page(desk, 'Tags') },
    { path: 'support/sla', lazy: page(desk, 'Sla') },
    { path: 'support/spam', lazy: page(desk, 'Spam') },
  ],
  clientNav: [
    {
      key: 'support',
      to: '/tickets',
      label: 'Support',
      order: 50,
      auth: true,
      // The sidebar's count: tickets we answered that wait for the client.
      badge: {
        key: 'tickets-answered',
        label: 'nav.badge.tickets',
        load: async () =>
          ((await api('/api/client/tickets?status=open&per_page=100')).data ?? []).filter(
            (x) => (x.status?.key ?? x.status) === 'answered',
          ).length,
      },
    },
  ],
  adminNav: [
    { section: 'support', to: '/admin/tickets', label: 'Tickets', permission: 'admin.tickets.read', order: 10 },
    {
      section: 'support',
      to: '/admin/support/departments',
      label: 'Departments',
      permission: 'admin.tickets.read',
      order: 20,
    },
    {
      section: 'support',
      to: '/admin/support/canned',
      label: 'Canned responses',
      permission: 'admin.tickets.reply',
      order: 30,
    },
    {
      section: 'support',
      to: '/admin/support/escalations',
      label: 'Escalations',
      permission: 'admin.support.escalations',
      order: 40,
    },
    { section: 'support', to: '/admin/support/sla', label: 'SLA targets', permission: 'admin.tickets.read', order: 45 },
    {
      section: 'support',
      to: '/admin/support/tags',
      label: 'Ticket tags',
      permission: 'admin.tickets.read',
      order: 46,
    },
    { section: 'support', to: '/admin/support/spam', label: 'Spam', permission: 'admin.tickets.read', order: 47 },
  ],
  clientWidgets: [{ key: 'recent-tickets', order: 30, Component: deferred(client, 'RecentTicketsWidget') }],
  adminWidgets: [
    {
      key: 'open-tickets',
      order: 20,
      permission: 'admin.tickets.read',
      Component: deferred(admin, 'OpenTicketsWidget'),
    },
  ],
  adminClientTabs: [
    {
      key: 'tickets',
      label: 'Tickets',
      order: 40,
      permission: 'admin.tickets.read',
      Component: deferred(admin, 'ClientTicketsTab'),
    },
  ],
};
